export interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
  user_type?: string;
}

const setLocalUserInfo = (userInfo: UserInfo) => {
  localStorage.setItem("userInfo", JSON.stringify(userInfo));
};

const getUserInfo = (): UserInfo | null => {
  const userInfo = localStorage.getItem("userInfo");
  return userInfo ? (JSON.parse(userInfo) as UserInfo) : null;
};

const setLocalTheme = (theme: string) => {
  localStorage.setItem("theme", theme);
};

const getTheme = () => {
  const theme = localStorage.getItem("theme");
  return theme ?? "light";
};

const setLocalLanguage = (language: string) => {
  localStorage.setItem("language", language);
};

const getLanguage = () => {
  const language = localStorage.getItem("language");
  return language ?? "en";
};

const setLocalTenantId = (tenantId: string) => {
  localStorage.setItem("tenantId", tenantId);
};

/** Tenant UUID from build-time env (Vite); strips optional quotes from .env values. */
const getEnvTenantId = (): string | null => {
  const raw = import.meta.env.VITE_X_TENANTID;
  if (raw === undefined || raw === null || raw === "") {return null;}
  const s = String(raw).replace(/^["']|["']$/g, "").trim();
  return s || null;
};

/**
 * If VITE_X_TENANTID is set, remove it from the pathname once so routes work with basename "/"
 * (e.g. /b8512e77-.../admin → /admin).
 */
const stripEnvTenantPrefixFromUrl = (): void => {
  const envId = getEnvTenantId();
  if (!envId) {return;}
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  if (pathParts[0] !== envId || pathParts.length < 2) {return;}
  const nextPath = `/${pathParts.slice(1).join("/")}`;
  window.history.replaceState(
    window.history.state,
    "",
    `${nextPath}${window.location.search}${window.location.hash}`,
  );
};

const getTenantId = () => {
  const fromEnv = getEnvTenantId();
  if (fromEnv) {return fromEnv;}
  const tenantId = localStorage.getItem("tenantId");
  return tenantId ?? null;
};
const setLocalAccessToken = (accessToken: string) => {
  localStorage.setItem("accessToken", accessToken);
};

const getAccessToken = () => {
  const accessToken = localStorage.getItem("accessToken");
  return accessToken ?? null;
};

const setLocalRefreshToken = (refreshToken: string) => {
  localStorage.setItem("refreshToken", refreshToken);
};

const getRefreshToken = () => {
  const refreshToken = localStorage.getItem("refreshToken");
  return refreshToken ?? null;
};

const setLocalUserId = (userId: string) => {
  localStorage.setItem("userid", userId);
};

const getUserId = () => {
  return localStorage.getItem("userid");
};

const setLocalCommunityId = (communityId: string) => {
  localStorage.setItem("community_id", communityId);
};

const getCommunityId = () => {
  return localStorage.getItem("community_id");
};

const clearLocalStorage = () => {
  const preserve: string[] = ["theme", "language", "tenantId"];
  const saved: Record<string, string> = {};
  preserve.forEach(k => {
    const v = localStorage.getItem(k);
    if (v !== null) {saved[k] = v;}
  });
  localStorage.clear();
  Object.entries(saved).forEach(([k, v]) => localStorage.setItem(k, v));
};

export {
  clearLocalStorage,
  getAccessToken,
  getCommunityId,
  getEnvTenantId,
  getLanguage,
  getRefreshToken,
  getTenantId,
  getTheme,
  getUserId,
  getUserInfo,
  setLocalAccessToken,
  setLocalCommunityId,
  setLocalLanguage,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalTheme,
  setLocalUserId,
  setLocalUserInfo,
  stripEnvTenantPrefixFromUrl,
};
